/*
 * Copyright (c) 2026 Gabriele Bonora. Tutti i diritti riservati.
 *
 * Software proprietario. Nessuna licenza d'uso, copia, modifica o distribuzione
 * e' concessa in assenza di autorizzazione scritta del titolare dei diritti.
 * Vedere il file LICENSE nella radice del progetto.
 */
import { useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, MapPin, Calendar, User, Upload, Trash2, FileText, Film, Image as ImageIcon, Clock, AlertTriangle } from 'lucide-react';
import ClienteLayout from '../components/ClienteLayout';
import { interventiAPI } from '../api/interventi';
import { filesAPI } from '../api/files';
import { fmtDataOra } from '../utils/date';
import { fmtDurata, fmtDurataOre, secondiSessione, secondiTotaliSessioni } from '../utils/durata';
import { useViewport } from '../hooks/useViewport';

const MAX_FILE_MB = 50;
const ACCENT = '#3B82F6';
const UPLOADS_URL = process.env.REACT_APP_UPLOADS_URL || '';
const STATO_LABELS = { in_attesa: 'In attesa', confermato: 'Confermato', in_corso: 'In corso', completato: 'Completato', annullato: 'Annullato' };
const PRIO = { urgente: { bg: '#FEE2E2', color: '#991B1B', label: 'Urgente' }, alta: { bg: '#FED7AA', color: '#9A3412', label: 'Alta' }, normale: { bg: '#E0E7FF', color: '#3730A3', label: 'Normale' }, bassa: { bg: '#F3F4F6', color: '#4B5563', label: 'Bassa' } };

export default function InterventoDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isMobile } = useViewport();
  const fileInput = useRef(null);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [uploadError, setUploadError] = useState('');

  const { data: i, isLoading } = useQuery({ queryKey: ['intervento-cliente', id], queryFn: () => interventiAPI.detail(id) });
  const { data: files = [] } = useQuery({ queryKey: ['files', id], queryFn: () => filesAPI.listByIntervento(id) });
  const { data: stats } = useQuery({ queryKey: ['files-stats', id], queryFn: () => filesAPI.stats(id) });

  const uploadMutation = useMutation({
    mutationFn: (file) => filesAPI.upload(id, file, 'foto', setUploadProgress),
    onSuccess: () => {
      setUploadProgress(null);
      queryClient.invalidateQueries({ queryKey: ['files', id] });
      queryClient.invalidateQueries({ queryKey: ['files-stats', id] });
    },
    onError: (err) => { setUploadProgress(null); setUploadError(err.response?.data?.error || 'Errore upload'); },
  });

  const deleteMutation = useMutation({
    mutationFn: (fileId) => filesAPI.remove(fileId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['files', id] });
      queryClient.invalidateQueries({ queryKey: ['files-stats', id] });
    },
  });


  const handleFiles = (e) => {
    setUploadError('');
    const selected = Array.from(e.target.files);
    const disponibileMb = stats ? stats.disponibile_mb : MAX_FILE_MB;

    for (const f of selected) {
      const fileMb = f.size / 1024 / 1024;

      // Limite per singolo file
      if (fileMb > MAX_FILE_MB) {
        setUploadError(`"${f.name}" è troppo grande (${fileMb.toFixed(1)}MB). Il limite per file è ${MAX_FILE_MB}MB.`);
        e.target.value = '';
        return;
      }
      // Limite spazio residuo dell'intervento
      if (fileMb > disponibileMb) {
        setUploadError(`Spazio insufficiente: "${f.name}" è ${fileMb.toFixed(1)}MB ma restano solo ${disponibileMb.toFixed(1)}MB per questo intervento.`);
        e.target.value = '';
        return;
      }
    }

    // Tutti i file ok: carica
    selected.forEach((f) => uploadMutation.mutate(f));
    e.target.value = '';
  };

  if (isLoading) return <ClienteLayout><div style={styles.loading}>Caricamento...</div></ClienteLayout>;
  if (!i) return <ClienteLayout><div style={styles.loading}>Intervento non trovato.</div></ClienteLayout>;

  const p = PRIO[i.priorita] || PRIO.normale;

  return (
    <ClienteLayout>
      <button onClick={() => navigate('/interventi')} style={styles.backBtn}><ArrowLeft size={16} /> Torna agli interventi</button>

      <div style={{ ...styles.headerCard, ...(isMobile ? { padding: '1.1rem' } : {}) }}>
        <div style={styles.badges}>
          <span style={{ ...styles.statoBadge, backgroundColor: i.stato_colore + '22', color: i.stato_colore }}>{STATO_LABELS[i.stato]}</span>
          <span style={{ ...styles.prioBadge, backgroundColor: p.bg, color: p.color }}>{i.priorita === 'urgente' && <AlertTriangle size={11} />} {p.label}</span>
          <code style={styles.code}>{i.codice}</code>
        </div>
        <h1 style={{ ...styles.title, ...(isMobile ? { fontSize: '1.2rem' } : {}) }}>{i.titolo}</h1>
        {i.descrizione && <p style={styles.desc}>{i.descrizione}</p>}
        <div style={styles.metaGrid}>
          {i.data_appuntamento && <div><Calendar size={14} /> Appuntamento: {fmtDataOra(i.data_appuntamento)}</div>}
          <div><Clock size={14} /> Richiesto: {fmtDataOra(i.data_richiesta)}</div>
          {i.ore_totali > 0 && <div><Clock size={14} /> Ore lavorate: {fmtDurataOre(i.ore_totali)}</div>}
          {(i.indirizzo || i.citta) && <div><MapPin size={14} /> {[i.indirizzo, i.citta, i.cap].filter(Boolean).join(', ')}</div>}
          {i.numero_tecnici > 0 && <div><User size={14} /> {i.numero_tecnici === 1 ? 'Un tecnico assegnato' : `${i.numero_tecnici} tecnici assegnati`}</div>}
        </div>
        {i.note_cliente && <div style={styles.noteBox}><strong>Note:</strong> {i.note_cliente}</div>}
      </div>

      {/* Lavoro svolto: sessione per sessione, senza dire quale tecnico l'ha fatta */}
      {i.sessioni && i.sessioni.length > 0 && (
        <div style={{ ...styles.section, ...(isMobile ? { padding: '1.1rem' } : {}) }}>
          <div style={styles.sectionHeader}>
            <h3 style={styles.sectionTitle}><Clock size={18} /> Lavoro svolto</h3>
            <span style={styles.oreTotali}>{fmtDurata(secondiTotaliSessioni(i.sessioni))} in totale</span>
          </div>
          <p style={styles.sessioniIntro}>
            Ogni riga è un intervento sul posto: quando è iniziato, quanto è durato e cosa è stato fatto.
          </p>
          <div style={styles.sessioniList}>
            {i.sessioni.map((s, idx) => (
              <div key={s.id} style={styles.sessioneCard}>
                <div style={styles.sessioneHead}>
                  <span style={styles.sessioneNum}>{i.sessioni.length - idx}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={styles.sessioneData}>{fmtDataOra(s.inizio)}</div>
                    <div style={styles.sessionePausa}>
                      {s.tecnico && <>{s.tecnico}{s.pausa_minuti > 0 ? ' · ' : ''}</>}
                      {s.pausa_minuti > 0 && <>pausa di {s.pausa_minuti} min esclusa</>}
                    </div>
                  </div>
                  <span style={styles.sessioneDurata}>{fmtDurata(secondiSessione(s))}</span>
                </div>
                <div style={styles.sessioneLavoro}>
                  {s.lavoro_svolto || <span style={styles.sessioneVuota}>Nessun dettaglio registrato per questa sessione.</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ ...styles.section, ...(isMobile ? { padding: '1.1rem' } : {}) }}>
        <div style={styles.sectionHeader}>
          <h3 style={styles.sectionTitle}><ImageIcon size={18} /> Foto e documenti</h3>
          {stats && <span style={styles.spaceInfo}>{stats.usato_mb} / {stats.limite_mb} MB</span>}
        </div>

        {stats && (
          <div style={styles.progressBar}>
            <div style={{ ...styles.progressFill, width: `${Math.min(100, (stats.usato_mb / stats.limite_mb) * 100)}%` }} />
          </div>
        )}

        <input ref={fileInput} type="file" accept="image/*,video/*,application/pdf" multiple onChange={handleFiles} style={{ display: 'none' }} />
        <button onClick={() => fileInput.current?.click()} disabled={uploadProgress !== null} style={{ ...styles.uploadBtn, ...(isMobile ? { width: '100%', justifyContent: 'center' } : {}) }}>
          <Upload size={16} /> {uploadProgress !== null ? `Caricamento ${uploadProgress}%...` : 'Carica foto/file'}
        </button>

        {uploadProgress !== null && (
          <div style={styles.progressBar}><div style={{ ...styles.progressFill, width: `${uploadProgress}%`, backgroundColor: ACCENT }} /></div>
        )}
        {uploadError && <div style={styles.errorBox}>{uploadError}</div>}

        {files.length === 0 ? (
          <div style={styles.emptyFiles}>Nessun file caricato. Aggiungi foto del problema per aiutare il tecnico.</div>
        ) : (
          <div style={styles.filesGrid}>
            {files.map((f) => (
              <FileCard key={f.id} file={f} onDelete={() => { if (window.confirm('Eliminare questo file?')) deleteMutation.mutate(f.id); }} />
            ))}
          </div>
        )}
      </div>
    </ClienteLayout>
  );
}

function FileCard({ file, onDelete }) {
  const isImg = file.mime_type?.startsWith('image/');
  const isVideo = file.mime_type?.startsWith('video/');
  const fullUrl = `${UPLOADS_URL}/${file.path?.replace('/uploads/', '') || file.nome_file}`;

  const processing = file.stato_processamento === 'processing' || file.stato_processamento === 'pending';

  return (
    <div style={styles.fileCard}>
      {processing ? (
        <div style={styles.fileProcessing}><Clock size={28} color="#9CA3AF" /><div style={{ fontSize: '0.75rem', marginTop: '0.3rem' }}>In elaborazione...</div></div>
      ) : isImg ? (
        <a href={fullUrl} target="_blank" rel="noreferrer"><img src={fullUrl} alt={file.nome_originale} style={styles.fileImg} /></a>
      ) : isVideo ? (
        <a href={fullUrl} target="_blank" rel="noreferrer" style={styles.fileIconBox}><Film size={28} color="#6B7280" /><span style={styles.fileIconLabel}>Video</span></a>
      ) : (
        <a href={fullUrl} target="_blank" rel="noreferrer" style={styles.fileIconBox}><FileText size={28} color="#6B7280" /><span style={styles.fileIconLabel}>PDF</span></a>
      )}
      <div style={styles.fileFooter}>
        <span style={styles.fileName}>{file.nome_originale}</span>
        <button onClick={onDelete} style={styles.delBtn} title="Elimina"><Trash2 size={13} /></button>
      </div>
    </div>
  );
}

const styles = {
  loading: { padding: '3rem', textAlign: 'center', color: '#6B7280' },
  backBtn: { display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.5rem 0.9rem', backgroundColor: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', cursor: 'pointer', marginBottom: '1rem', fontSize: '0.88rem' },
  headerCard: { backgroundColor: 'white', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', marginBottom: '1.25rem' },
  badges: { display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.6rem', flexWrap: 'wrap' },
  statoBadge: { padding: '0.25rem 0.7rem', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 700 },
  prioBadge: { display: 'inline-flex', alignItems: 'center', gap: '0.2rem', padding: '0.2rem 0.55rem', borderRadius: '6px', fontSize: '0.74rem', fontWeight: 600 },
  code: { backgroundColor: '#E5E7EB', padding: '0.15rem 0.45rem', borderRadius: '4px', fontSize: '0.8rem', fontFamily: 'ui-monospace, monospace' },
  title: { margin: '0 0 0.5rem', fontSize: '1.4rem', fontWeight: 700 },
  desc: { color: '#4B5563', margin: '0 0 1rem' },
  metaGrid: { display: 'flex', flexDirection: 'column', gap: '0.5rem', fontSize: '0.88rem', color: '#4B5563' },
  noteBox: { marginTop: '1rem', padding: '0.75rem', backgroundColor: '#F9FAFB', borderRadius: '8px', fontSize: '0.88rem', color: '#4B5563' },
  oreTotali: { fontWeight: 700, color: ACCENT, fontSize: '0.9rem', whiteSpace: 'nowrap' },
  sessioniIntro: { margin: '0 0 0.85rem', fontSize: '0.84rem', color: '#6B7280', lineHeight: 1.45 },
  sessioniList: { display: 'flex', flexDirection: 'column', gap: '0.6rem' },
  sessioneCard: { border: '1px solid #E5E7EB', borderRadius: '10px', padding: '0.85rem', backgroundColor: '#FFFFFF' },
  sessioneHead: { display: 'flex', alignItems: 'center', gap: '0.65rem', marginBottom: '0.5rem' },
  sessioneNum: { width: '24px', height: '24px', flexShrink: 0, borderRadius: '50%', backgroundColor: ACCENT + '18', color: ACCENT, fontSize: '0.78rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  sessioneData: { fontWeight: 600, fontSize: '0.9rem' },
  sessionePausa: { fontSize: '0.78rem', color: '#9CA3AF', marginTop: '0.1rem' },
  sessioneDurata: { fontWeight: 700, fontSize: '0.9rem', color: '#111827', whiteSpace: 'nowrap', flexShrink: 0 },
  sessioneLavoro: { fontSize: '0.88rem', color: '#374151', lineHeight: 1.5, paddingLeft: 'calc(24px + 0.65rem)', whiteSpace: 'pre-wrap' },
  sessioneVuota: { color: '#9CA3AF', fontStyle: 'italic' },
  section: { backgroundColor: 'white', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
  sectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' },
  sectionTitle: { margin: 0, fontSize: '1.05rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.4rem' },
  spaceInfo: { fontSize: '0.82rem', color: '#6B7280', fontWeight: 600 },
  progressBar: { height: '6px', backgroundColor: '#F3F4F6', borderRadius: '3px', overflow: 'hidden', marginBottom: '0.85rem' },
  progressFill: { height: '100%', backgroundColor: '#10B981', transition: 'width 0.3s' },
  uploadBtn: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem', padding: '0.7rem 1.2rem', backgroundColor: ACCENT, color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, marginBottom: '1rem', minHeight: '44px' },
  errorBox: { backgroundColor: '#FEE2E2', color: '#991B1B', padding: '0.65rem', borderRadius: '8px', fontSize: '0.85rem', marginBottom: '1rem' },
  emptyFiles: { padding: '2rem', textAlign: 'center', color: '#9CA3AF', fontSize: '0.9rem', backgroundColor: '#F9FAFB', borderRadius: '8px' },
  filesGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '0.7rem' },
  fileCard: { backgroundColor: '#F9FAFB', borderRadius: '10px', overflow: 'hidden' },
  fileImg: { width: '100%', height: '120px', objectFit: 'cover', display: 'block', cursor: 'pointer' },
  fileIconBox: { height: '120px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '0.3rem', textDecoration: 'none', backgroundColor: '#F3F4F6' },
  fileIconLabel: { fontSize: '0.75rem', color: '#6B7280', fontWeight: 600 },
  fileProcessing: { height: '120px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#9CA3AF', backgroundColor: '#F3F4F6' },
  fileFooter: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem 0.65rem', gap: '0.4rem' },
  fileName: { fontSize: '0.75rem', color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 },
  delBtn: { padding: '0.3rem', backgroundColor: 'transparent', border: 'none', color: '#DC2626', cursor: 'pointer', display: 'inline-flex', flexShrink: 0 },
};
